// Presentation rules for ResolvedServiceUnit.source (IBM-972): the backend
// always resolves a unit through the chain NODE → ANCESTOR_NODE → CLIENT →
// REGION_FALLBACK → GLOBAL_FALLBACK and reports which step answered. The two
// *_FALLBACK values mean nobody chose that unit for this customer — they must
// never be presented as a dedicated assignment ("Your service centre: X").
//
// Wording: the UI speaks of Service Centers and Service Departments
// (`ServiceUnit.type`); "service unit" is backend naming only.

export type TranslateFn = (key: string, options?: Record<string, unknown>) => string;

export interface ServiceUnitSourceInfo {
    // section/card heading — fallbacks get a "central service" heading instead
    // of one implying a dedicated assignment
    title: string;
    // short chip text describing where the unit came from
    label: string;
    // caption explaining a fallback; empty for real assignments
    caption: string;
    isFallback: boolean;
}

export interface ServiceUnitSourceOptions {
    // title of the ancestor node for ANCESTOR_NODE
    ancestorTitle?: string | null;
    // CENTER | DEPARTMENT — picks the wording of the dedicated-assignment title
    unitType?: string | null;
    // 'customer' (default): the viewer is served by this unit, so the wording may
    // say "your company"/"your region". 'provider': the viewer belongs to the
    // service organisation and looks at someone else's element — the same
    // sentences then have to speak about *this* company/region.
    perspective?: 'customer' | 'provider';
}

const dedicatedTitle = (t: TranslateFn, unitType?: string | null): string =>
    unitType === 'DEPARTMENT'
        ? t('Responsible Service Department')
        : t('Responsible Service Center');

export const getServiceUnitSourceInfo = (
    source: string | null | undefined,
    t: TranslateFn,
    options: ServiceUnitSourceOptions = {}
): ServiceUnitSourceInfo => {
    const {ancestorTitle, unitType, perspective = 'customer'} = options;
    const isProvider = perspective === 'provider';
    switch (source) {
        case 'NODE':
            return {
                title: dedicatedTitle(t, unitType),
                label: t('Assigned to this element'),
                caption: '',
                isFallback: false,
            };
        case 'ANCESTOR_NODE':
            return {
                title: dedicatedTitle(t, unitType),
                label: ancestorTitle
                    ? t('Inherited from "{{title}}"', {title: ancestorTitle})
                    : t('Inherited'),
                caption: '',
                isFallback: false,
            };
        case 'CLIENT':
            return {
                title: dedicatedTitle(t, unitType),
                label: isProvider ? t('Assigned to this company') : t('Assigned to your company'),
                caption: '',
                isFallback: false,
            };
        case 'REGION_FALLBACK':
            return {
                title: t('Central service contact'),
                label: t('Regional default service'),
                caption: isProvider
                    ? t('No dedicated Service Center has been assigned — this is the shared default for this region.')
                    : t('No dedicated Service Center has been assigned — this is the shared default for your region.'),
                isFallback: true,
            };
        case 'GLOBAL_FALLBACK':
            return {
                title: t('Central service contact'),
                label: t('Central service'),
                caption: t('No dedicated Service Center has been assigned — this is the central service contact.'),
                isFallback: true,
            };
        default:
            // Unknown source: presenting it as a fallback is the safe direction —
            // a fallback must never look like a dedicated assignment, not vice versa.
            return {
                title: t('Central service contact'),
                label: '',
                caption: '',
                isFallback: true,
            };
    }
};
